import type { HardwareGraph } from '../types';

export function PrintHandout({ graph, source, filename, graphImage }: { graph: HardwareGraph; source: string; filename: string; graphImage?: string }) {
  const registers = graph.nodes.filter((node) => node.type === 'register');
  return <article className="print-handout" aria-hidden="true">
    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.5rem' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <img 
            src="/dark_logo.jpeg" 
            alt="Ortl Logo" 
            style={{ height: '38px', width: 'auto', objectFit: 'contain', display: 'block' }} 
          />
          <small style={{ letterSpacing: '0.08em', fontWeight: 600, fontSize: '0.85rem' }}>/ DESIGN HANDOUT</small>
        </div>
        <h1>{graph.module}</h1>
        <p>{filename} · Hardware IR schema {graph.schemaVersion}</p>
      </div>
      <dl>
        <div><dt>Ports</dt><dd>{graph.ports.length}</dd></div>
        <div><dt>Components</dt><dd>{graph.nodes.length}</dd></div>
        <div><dt>Nets</dt><dd>{graph.edges.length}</dd></div>
        <div><dt>Registers</dt><dd>{registers.length}</dd></div>
      </dl>
    </header>
    <section>
      <h2>Elaborated schematic</h2>
      {graphImage ? <img src={graphImage} alt={`Elaborated schematic for ${graph.module}`} /> : <p>Use “Print handout” from the workbench to render the current graph view.</p>}
    </section>
    <section className="print-tables">
      <div>
        <h2>Port map</h2>
        <table>
          <thead>
            <tr><th>Port</th><th>Direction</th><th>Width</th></tr>
          </thead>
          <tbody>
            {graph.ports.map((port) => (
              <tr key={port.id}>
                <td>{port.label}</td>
                <td>{String(port.properties.direction ?? port.type)}</td>
                <td>{port.width > 1 ? `[${port.width - 1}:0]` : '1'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h2>Register map</h2>
        <table>
          <thead>
            <tr><th>Register</th><th>Width</th><th>Clock / reset</th></tr>
          </thead>
          <tbody>
            {registers.map((node) => (
              <tr key={node.id}>
                <td>{node.label}</td>
                <td>{node.width}</td>
                <td>{String(node.properties.clock ?? '—')} / {String(node.properties.reset ?? '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
    {!!graph.fsms?.length && (
      <section>
        <h2>State machines</h2>
        {graph.fsms.map((fsm) => (
          <div className="print-fsm" key={fsm.id}>
            <b>{fsm.name}</b>
            <span>{fsm.states.map((state) => state.name).join(' → ')}</span>
            <small>{fsm.transitions.map((transition) => `${transition.source} → ${transition.target} [${transition.condition}]`).join(' · ')}</small>
          </div>
        ))}
      </section>
    )}
    <section className="print-source">
      <h2>RTL source — {filename}</h2>
      <pre>{source}</pre>
    </section>
    <footer>Generated by Ortl from Hardware IR · Review inferred behavior before sign-off.</footer>
  </article>;
}